import { validateCPF_CNPJ } from "./CPF_CNPJValidation";
import { cleanPhoneMask, cleanTaxIDMask, cpfMask, progressiveCnpjMask, phoneMask } from "./masks";

const PIX_EVP_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const stripSpaces = (value) => String(value || "").replace(/\s/g, "");

/** Labels for explicit chips + legacy `document` (auto-detect / favorites). */
export const PIX_KEY_TYPE_LABELS = {
  cpf: "CPF",
  cnpj: "CNPJ",
  document: "CPF/CNPJ",
  phone: "Celular",
  email: "E-mail",
  randomKey: "Aleatória",
};

const isValidDocument = (value) => validateCPF_CNPJ(value) === true;

/** Celular BR: DDD (2) + 9 + 8 dígitos — 3º dígito é sempre 9 */
const looksLikeMobileDigits = (digits) =>
  digits.length >= 3 && digits[2] === "9";

const hasPhoneMask = (value) => /^\(\d{2}\)/.test(value) || /^\+/.test(value);

/**
 * Máscara de CPF/CNPJ — ignora quando os dígitos seguem padrão de celular
 * (ex.: 119.999.900-00 na verdade é 11 99999-0000).
 */
const hasDocumentMask = (value) => {
  if (!/[./]/.test(value)) return false;

  const digits = value.replace(/\D/g, "");
  if (
    digits.length === 11 &&
    looksLikeMobileDigits(digits) &&
    !isValidDocument(value)
  ) {
    return false;
  }

  return true;
};

/**
 * Maps legacy `document` (or length-only) to explicit `cpf` / `cnpj` for POST /v1/ib/pix.
 */
export const resolvePixKeyApiType = (type, rawValue) => {
  const normalized = String(type || "").trim().toLowerCase();
  if (normalized === "cpf" || normalized === "cnpj") return normalized;
  if (normalized === "email" || normalized === "phone") return normalized;
  if (
    normalized === "randomkey" ||
    normalized === "random_key" ||
    normalized === "evp" ||
    normalized === "chave_aleatoria"
  ) {
    return "randomKey";
  }

  if (normalized === "document" || !normalized) {
    const digits = String(rawValue || "").replace(/\D/g, "");
    if (digits.length === 14) return "cnpj";
    if (digits.length === 11) return "cpf";
  }

  return type || null;
};

/**
 * Infere o tipo da chave Pix a partir do valor digitado (regex/heurísticas).
 * Prefer explicit chips in transfer UI; this remains for paste/favorites fallback.
 */
export const detectPixKeyType = (rawValue) => {
  const value = String(rawValue || "").trim();
  if (!value) return null;

  if (value.includes("@")) {
    return "email";
  }

  const compactValue = stripSpaces(value);
  if (PIX_EVP_REGEX.test(compactValue)) {
    return "randomKey";
  }

  const digitsOnly = value.replace(/\D/g, "");

  if (digitsOnly.length === 11 && isValidDocument(value)) {
    return "cpf";
  }
  if (digitsOnly.length === 14 && isValidDocument(value)) {
    return "cnpj";
  }

  if (hasPhoneMask(value)) {
    return "phone";
  }

  if (hasDocumentMask(value)) {
    return digitsOnly.length > 11 ? "cnpj" : "cpf";
  }

  if (digitsOnly.length > 0) {
    if (digitsOnly.length === 14) {
      return "cnpj";
    }

    if (digitsOnly.length === 11) {
      if (looksLikeMobileDigits(digitsOnly)) {
        return "phone";
      }
      return "cpf";
    }

    if (digitsOnly.length === 10) {
      return "phone";
    }

    if (digitsOnly.length >= 12 && digitsOnly.startsWith("55")) {
      return "phone";
    }

    return null;
  }

  if (/^[0-9a-f-]+$/i.test(compactValue) && compactValue.length >= 8) {
    return "randomKey";
  }

  if (/[a-zA-Z]/.test(value)) {
    return "email";
  }

  return null;
};

/** Display mask while typing for an explicit chip type. */
export const formatPixKeyInput = (rawValue, type) => {
  const value = String(rawValue || "");
  if (!value) return "";

  switch (type) {
    case "cpf":
      return cpfMask(value.replace(/\D/g, "").slice(0, 11));
    case "cnpj":
      return progressiveCnpjMask(value);
    case "document":
      return value.replace(/\D/g, "").length > 11
        ? progressiveCnpjMask(value)
        : cpfMask(value.replace(/\D/g, "").slice(0, 14));
    case "phone": {
      if (value.startsWith("+")) {
        const digits = value.replace(/\D/g, "");
        return digits.startsWith("55") ? `+${digits.slice(0, 13)}` : `+${digits}`;
      }
      const digits = value.replace(/\D/g, "").slice(-11);
      return phoneMask(digits);
    }
    case "email":
      return value.trimStart();
    case "randomKey":
      return stripSpaces(value);
    default:
      return value;
  }
};

export const normalizePixKeyValue = (rawValue, type) => {
  const value = String(rawValue || "").trim();
  if (!value || !type) return "";

  const apiType = resolvePixKeyApiType(type, value);

  switch (apiType) {
    case "cpf":
    case "cnpj":
    case "document":
      return cleanTaxIDMask(value).replace(/\D/g, "");
    case "phone": {
      // DICT / PaySmart expect E.164 with leading '+' (e.g. +5511911651012).
      let digits = value.replace(/\D/g, "");
      if (digits.startsWith("55") && digits.length >= 12) {
        return `+${digits}`;
      }
      if (digits.startsWith("55")) {
        digits = digits.slice(2);
      }
      const local = cleanPhoneMask(digits.length > 11 ? digits.slice(-11) : digits);
      return `+55${local}`;
    }
    case "email":
      return value.toLowerCase();
    case "randomKey":
      return stripSpaces(value);
    default:
      return value;
  }
};

export const validatePixKey = (rawValue, type) => {
  const value = String(rawValue || "").trim();
  if (!value) {
    return { valid: false, message: "Informe a chave Pix." };
  }

  const digitsOnly = value.replace(/\D/g, "");
  let resolvedType = type || detectPixKeyType(value);

  if (
    !type &&
    resolvedType === "phone" &&
    digitsOnly.length === 11 &&
    isValidDocument(value)
  ) {
    resolvedType = "cpf";
  }

  if (!resolvedType) {
    return { valid: false, message: "Não foi possível identificar o tipo da chave." };
  }

  const apiType = resolvePixKeyApiType(resolvedType, value) || resolvedType;

  switch (apiType) {
    case "email":
      if (!EMAIL_REGEX.test(value)) {
        return { valid: false, message: "E-mail inválido." };
      }
      break;
    case "cpf": {
      if (digitsOnly.length !== 11 || validateCPF_CNPJ(value) !== true) {
        return { valid: false, message: "CPF inválido." };
      }
      break;
    }
    case "cnpj": {
      if (digitsOnly.length !== 14 || validateCPF_CNPJ(value) !== true) {
        return { valid: false, message: "CNPJ inválido." };
      }
      break;
    }
    case "document": {
      const isValid = validateCPF_CNPJ(value);
      if (isValid !== true) {
        return { valid: false, message: "CPF/CNPJ inválido." };
      }
      break;
    }
    case "phone": {
      const digits = value.replace(/\D/g, "").replace(/^55/, "");
      if (digits.length < 10 || digits.length > 11) {
        return { valid: false, message: "Celular inválido." };
      }
      if (digits.length === 11 && digits[2] !== "9") {
        return { valid: false, message: "Celular inválido." };
      }
      break;
    }
    case "randomKey":
      if (!PIX_EVP_REGEX.test(stripSpaces(value))) {
        return { valid: false, message: "Chave aleatória inválida." };
      }
      break;
    default:
      return { valid: false, message: "Chave Pix inválida." };
  }

  return {
    valid: true,
    type: apiType,
    value: normalizePixKeyValue(value, apiType),
  };
};
